/* eslint-disable react/jsx-props-no-spreading */

import { Editable } from "slate-react";
import React, { useCallback } from "react";
import { getMarked } from "../utils/SlateUtilityFunctions";
import styles from '../ChatInput.module.scss';
import { isIntegration, isThread } from "../../../../types";
import { usePolyglot } from "../../../../context/Polyglot";
import { useChatInputKeydown } from "./hooks";
import TagPopup from "../TagPopup";
import { useConversationContext } from "../../../../context/ChatContext/contextConvo";
import { useConversationName } from "../../../../components/sidebars/chat/Conversation/ConversationRow";
import { useTypedDispatch } from "../../../../redux/hooks";
import { addOneToast } from "../../../../redux/slices";

const MAX_TOTAL_FILE_SIZE = 40 * 1024 * 1024;

const Element = ({ attributes, children, element }) => {
    const style = { textAlign: element.align }
    switch (element.type) {
        case 'bulleted-list':
            return (
                <ul style={style} {...attributes}>
                    {children}
                </ul>
            )
        case 'list-item':
            return (
                <li style={style} {...attributes}>
                    {children}
                </li>
            )
        case 'numbered-list':
            return (
                <ol style={style} {...attributes}>
                    {children}
                </ol>
            )
        default:
            return (
                <p style={style} {...attributes}>
                    {children}
                </p>
            )
    }
}

const Leaf = ({ attributes, children, leaf }) => {
    // eslint-disable-next-line no-param-reassign
    children = getMarked(leaf, children);
    return <span {...attributes}>{children}</span>
}

interface Props {
    handleSubmit: (val: any) => void
}

export const Editor: React.FC<Props> = ({ handleSubmit }) => {
    const { conversation, useChatAction, files } = useConversationContext();
    const dispatch = useTypedDispatch();
    const { t } = usePolyglot();

    const conversationName = useConversationName(conversation);

    const isChatIntegration = isIntegration(conversation);
    const {
        handleKeyDown
    } = useChatInputKeydown(handleSubmit);

    const renderElement = useCallback(props => <Element {...props} />, []);

    const renderLeaf = useCallback(props => <Leaf {...props} />, []);

    const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
        const clipboardData = event.clipboardData || (event as any).dataTransfer;
        const pastedFiles = (await clipboardData.files) || clipboardData.items;

        if (!isChatIntegration && pastedFiles?.length > 0) {
            event.preventDefault();

            const currentSize = files.reduce((sum, f) => sum + f.size, 0);
            const newSize = Array.from(pastedFiles).reduce((sum, f) => sum + f.size, 0);

            if (currentSize + newSize > MAX_TOTAL_FILE_SIZE) {
                dispatch(addOneToast({
                    content: "Total file size must be under 40 MB",
                    title: "Files too large",
                    type: "error"
                }));
                return;
            }

            useChatAction({
                type: 'add_file_to_upload',
                payload: pastedFiles
            });
        }
    };

    return (
        <>
            <TagPopup />
            <Editable
                onPaste={handlePaste}
                placeholder={isThread(conversation) ? t("actions.reply_in_thread") : t('actions.message_%name', { name: conversationName })}
                renderPlaceholder={({ children, attributes }) => (
                    <span className={styles.placeholder} {...attributes}>
                        {children}
                    </span>
                )}
                autoFocus
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                onKeyDown={handleKeyDown}
                tabIndex={0}
            />
        </>
    )
}

export default Editor;
